<script>
import baseMixin from '@/mixins/baseMixin'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import DownloadedFile from '@/models/file/downloadedFile'
import FilePreviewGalleryEmptyBlock from './file-preview-gallery-empty-block.vue'
import FilePreviewGalleryDialogToolbar from './file-preview-gallery-dialog-toolbar.vue'
import FilePreviewContainer from './file-preview-container.vue'

export default {
  name: 'FilePreviewGalleryDialog',
  components: {
    FilePreviewGalleryEmptyBlock,
    FilePreviewGalleryDialogToolbar,
    FilePreviewContainer,
  },
  mixins: [baseMixin],
  model: {
    prop: 'show',
    event: 'showUpdated',
  },
  props: {
    show: { type: Boolean, required: true },
    files: {
      type: Array,
      default: () => [],
      validator: (prop) => prop.every((e) => e instanceof DownloadedFile),
    },
    downloadHandler: { type: Function, default: () => {} },
    preselect: { type: Number, default: null },
  },
  data: () => {
    return {
      carouselPosition: 0,
      dialog: true,
      filesLoading: [],
    }
  },
  computed: {
    hasFiles() {
      return !isNullOrEmptyArray(this.files)
    },
    hasMultipleFiles() {
      return this.hasFiles && this.files.length > 1
    },
    getFileByCarouselPosition() {
      if (!this.show) return
      if (!this.hasFiles) return
      return this.files[this.carouselPosition]
    },
    isCurrentFileAnImage() {
      const file = this.getFileByCarouselPosition
      return file?.file?.type ? file?.file?.type.includes('image') : false
    },
    isCurrentFileDownloadable() {
      const file = this.getFileByCarouselPosition
      return !!file && !!file?.file
    },
    compFileName() {
      const file = this.getFileByCarouselPosition
      return file?.name || ''
    },
  },
  watch: {
    show() {
      if (!this.show) return
      if (!this.hasFiles) return
      const fileToLoad = this.files[this.carouselPosition]
      if (!fileToLoad) return
      this.loadFile(fileToLoad.id)
    },
    preselect() {
      this.carouselPosition = this.preselect
    },
    carouselPosition() {
      if (!this.hasFiles) return
      const fileToLoad = this.files[this.carouselPosition]
      if (!fileToLoad) return
      this.loadFile(fileToLoad.id)
    },
  },
  methods: {
    close() {
      this.$emit('showUpdated', false)
    },
    isLoadingFile(fileId) {
      return this.filesLoading.includes(fileId)
    },
    startFileLoading(fileId) {
      this.filesLoading.push(fileId)
    },
    stopFileLoading(fileId) {
      const foundIndex = this.filesLoading.indexOf(fileId)
      if (foundIndex < 0) return
      this.filesLoading.splice(foundIndex, 1)
    },
    async loadFile(fileId) {
      this.startFileLoading(fileId)
      await this.downloadHandler(fileId)
      this.stopFileLoading(fileId)
    },
    download() {
      const file = this.getFileByCarouselPosition

      // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
      const blobUrl = URL.createObjectURL(file.file)

      // Create a link element
      const link = document.createElement('a')

      // Set link's href to point to the Blob URL
      link.href = blobUrl
      link.download = file.name
      link.setAttribute('target', '_blank')

      // Append link to the body
      document.body.appendChild(link)

      // Dispatch click event on the link
      // This is necessary as link.click() does not work on the latest firefox
      link.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        })
      )

      // Remove link from body
      document.body.removeChild(link)
    },
  },
}
</script>

<template>
  <v-overlay v-if="show" z-index="14" opacity=".95">
    <v-dialog
      :value="show"
      fullscreen
      hide-overlay
      transition="fade-transition"
      class="full-height"
      @keydown.esc="close"
    >
      <FilePreviewGalleryDialogToolbar
        :can-download-file="isCurrentFileDownloadable"
        :file-name="compFileName"
        @download="download"
        @closed="close"
      />
      <FilePreviewGalleryEmptyBlock
        v-if="!hasFiles"
        data-testid="file-preview-gallery-empty-block"
        @closed="close"
      />
      <v-carousel
        v-if="hasFiles"
        v-model="carouselPosition"
        class="full-height"
        height="100%"
        :show-arrows="hasMultipleFiles"
        :hide-delimiter-background="!hasMultipleFiles"
        :hide-delimiters="!hasMultipleFiles"
      >
        <v-carousel-item
          v-for="file in files"
          :key="`${file.id}_${file.name}`"
          class="full-height"
        >
          <v-sheet
            height="100%"
            tile
            class="full-height transparent d-flex align-top text-center justify-center"
          >
            <FilePreviewContainer
              :file="file"
              :loading="isLoadingFile(file.id)"
              @retry="loadFile(file.id)"
              @download="download"
            />
          </v-sheet>
        </v-carousel-item>
      </v-carousel>
    </v-dialog>
  </v-overlay>
</template>
